/* eslint-disable max-lines */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import P1 from '@base/P1/P1';
import H4 from '@base/H4/H4';
import H3 from '@base/H3/H3';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import Paper from 'aquilla/core/atoms/Paper';
import Dropdown from '@comp/Dropdown/Dropdown';
import BatchCardNew from '@comp/BatchCard/AquillaBatchCardNew';
import PlusCourseCard from '@comp/PlusCourseCard/AquillaPlusCourseCard';
import FreeCourseCard from '@comp/FreeCourseCard/FreeCourseCard';
import Dialog from '@comp/Dialog/Dialog';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { toggleEnrolledCourse, toggleBookmarkItem } from '@actions/courses';
import { useSnackbar } from '@stores/snackbar';
import { enrollInBatch } from '@actions/batches';
import { goBack } from '@utils/breadcrumbs';
import { getImage } from '@utils/imageHelper';
import { mediaQuery, Spacings } from '@styles/index';
import { getCourseEnrollMessage, programmeTypes } from '@constants/index';
import { useRouter } from 'next/router';
import CourseCarouselSkeleton from '@skeletons/CourseCard/CourseCarousel';
import PlatformBatchCard from '@comp/BatchCard/PlatFormBatchCard';
import { cardType } from '@cont/BatchGroup/constants';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import InfiniteFeedRow from '@cont/SWRInfiniteFeedRow/InfiniteFeedRow';
import { BATCH, BATCH_GROUPS, FREE } from './constants';

const Header = styled(Paper)`
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_6B};
  display: flex;
  grid-column: span 24;
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    box-shadow: none;
    background: var(--color-base-0);
    padding: 0;
    margin: 24px 0 0;
    display: flex;
    flex-direction: column;
  }
`;

const Educator = styled(H4)`
  margin-left: ${Spacings.SPACING_3B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    margin-top: ${Spacings.SPACING_6B};
    font-size: 22px;
    line-height: 120%;
  }
`;

const FilterWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin: 0 -${Spacings.SPACING_4B};
    grid-column: span 10;
    display: flex;
    overflow: auto;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_6B}
      ${Spacings.SPACING_4B};
    & > button {
      box-shadow: none;
      white-space: nowrap;
    }
    &.sticky {
      position: fixed;
      top: 48px;
      width: 100%;
      background: var(--color-i-white);
      box-shadow: inset 0px -1px 0px var(--color-shadow);
    }
  }
`;

const TopWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  position: sticky;
  top: 72px;
  z-index: 1;
  background: var(--color-base-0);
  margin-bottom: ${({ $isFilters }) => {
    return $isFilters ? `${Spacings.SPACING_8B}` : `${Spacings.SPACING_0B}`;
  }};
  ${mediaQuery.uptoMobile} {
    position: relative;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    top: 0;
  }
`;

const PlusCourse = styled.div`
  margin-bottom: ${Spacings.SPACING_8B};
  grid-column: span 6;
`;

const FreeCourse = styled(FreeCourseCard)`
  margin-bottom: ${Spacings.SPACING_8B};
`;

const Empty = styled.div`
  grid-column: 10/16;
  text-align: center;
  margin-top: ${Spacings.SPACING_24B};
`;

const NoCourses = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
`;

const PlatformBatch = styled(PlatformBatchCard)`
  margin-right: ${Spacings.SPACING_0B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const renderEmptyState = () => {
  return (
    <Empty>
      <EmptyImage src={`${getImage('empty.svg')}`} alt="" />
      <NoCourses>No courses found</NoCourses>
      <P1 color="var(--color-text-secondary)">
        The educator hasn’t added any courses yet. Try changing the filters
      </P1>
    </Empty>
  );
};

const isReachingEnd = (data) => {
  if (data === undefined) {
    return false;
  }

  return !data?.next || data?.results?.length >= data?.count;
};

const EducatorCourses = ({
  authorID,
  profileInfo,
  filtersApplied,
  setSortsApplied,
  filteredCoursesData,
  hideFilters,
  titleType,
  isPlatinumEducator
}) => {
  const router = useRouter();
  const { query } = router;
  const dispatch = useDispatchToStore();
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const selectedGoalUID = useSelectedGoalUID();
  const courseRef = useRef('');
  const filterWrapperRef = useRef('');
  const refetchingIntervalRef = useRef(null);

  const { open: showSnackbar } = useSnackbar();

  const {
    coursesByFilters: filteredCoursesResponse,
    isValidating: isFetchingFilteredCourses,
    updateCoursesListData,
    setSize: setNumberOfPages
  } = filteredCoursesData;

  const userName = authorID?.slice(1);

  const toggleEnrollForCourseInSWRData = (dataUID) => (data) => {
    // Find the index of the course among the results.
    let courseIndex = -1;
    let resultsIndex = -1;
    for (let i = 0; i < data?.length; i += 1) {
      courseIndex = data?.[i]?.results?.findIndex(
        (batch) => batch.uid === dataUID
      );
      if (courseIndex !== -1) {
        resultsIndex = i;
        break;
      }
    }
    const updatedData = data;
    if (courseIndex !== -1 && resultsIndex !== -1)
      updatedData[resultsIndex].results[courseIndex].isEnrolled =
        !updatedData[resultsIndex].results[courseIndex].isEnrolled;
    return updatedData;
  };

  const toggleEnroll =
    (course) => async (courseUID, isEnrolled, eventData, isRecordedGoal) => {
      if (isEnrolled) {
        courseRef.current = course;
        setUnEnrollModalOpen(true);
      } else {
        await dispatch(toggleEnrolledCourse(courseUID, !isEnrolled)).then(
          (res) => {
            if (!res?.error) {
              updateCoursesListData(toggleEnrollForCourseInSWRData(courseUID), {
                revalidate: false
              });
              const { uid: goalUID, name } = course?.goal || {};
              showSnackbar({
                message: getCourseEnrollMessage({
                  type:
                    course?.programmeType === programmeTypes.TESTSERIES
                      ? 'Test-series'
                      : 'Course',
                  goalName: name,
                  goalUID,
                  selectedGoalUID,
                  isRecordedGoal
                }),
                type: 'success'
              });
            }
          }
        );
      }
    };
  const acceptOnClick = () => {
    dispatch(toggleEnrolledCourse(courseRef.current.uid, false)).then((res) => {
      if (!res?.error) {
        updateCoursesListData(
          toggleEnrollForCourseInSWRData(courseRef.current),
          { revalidate: false }
        );
      }
    });
    setUnEnrollModalOpen(false);
  };
  const onCloseModal = () => setUnEnrollModalOpen(false);
  const message =
    'This course will be removed from your scehdule and you will stop receiving updates related to this course';

  // batch enroll modal state and handlers
  const batchUnEnrollMessage =
    'All courses from this batch will be removed from your schedule and you will stop receiving updates related to this batch';
  const [batchUnEnrollModalOpen, setBatchUnEnrollModalOpen] = useState(false);
  const batchRef = useRef('');
  const toggleBatchEnroll =
    (batch) => async (batchUid, isEnrolled, eventData, isRecordedGoal) => {
      if (isEnrolled) {
        batchRef.current = batch;
        setBatchUnEnrollModalOpen(true);
      } else {
        await dispatch(enrollInBatch(batchUid, !isEnrolled)).then((res) => {
          if (!res?.error) {
            const { uid, name } = batch?.goal || {};
            updateCoursesListData(toggleEnrollForCourseInSWRData(batchUid), {
              revalidate: false
            });
            showSnackbar({
              message: getCourseEnrollMessage({
                type: 'Courses',
                goalUID: uid,
                selectedGoalUID,
                goalName: name,
                isRecordedGoal
              }),
              type: 'success'
            });
          }
        });
      }
    };

  const acceptOnClickBatch = () => {
    dispatch(enrollInBatch(batchRef.current.uid, false)).then((res) => {
      if (!res?.error) {
        updateCoursesListData(
          toggleEnrollForCourseInSWRData(batchRef.current.uid),
          { revalidate: false }
        );
      }
    });
    setBatchUnEnrollModalOpen(false);
  };

  const onCloseBatchUnenrollModal = () => setBatchUnEnrollModalOpen(false);

  const getStripLength = {
    goal: 11,
    topic: 13
  };

  const onClickSave = (courseUID, isBookmarked) => {
    dispatch(toggleBookmarkItem(courseUID, 4, isBookmarked));
  };

  const renderCourse = (course) => {
    if (query.type === FREE) {
      return (
        <FreeCourse
          course={course}
          key={course.uid}
          onClickSave={onClickSave}
        />
      );
    }
    if (query.type === BATCH) {
      return (
        <PlusCourse>
          <BatchCardNew
            key={course.uid}
            batch={course}
            hideEnroll
            toggleEnroll={toggleBatchEnroll(course)}
            firstAuthorUsername={userName}
            dataAnalytics="educatorProfile"
          />
        </PlusCourse>
      );
    }
    if (query.type === BATCH_GROUPS) {
      return (
        <PlatformBatch
          cardType={cardType.SMALL}
          batch={course}
          key={course.uid}
          dataAnalytics="batch"
          lastPrimarySourceSection="Educator Profile"
          isFeedCard
        />
      );
    }
    return (
      <PlusCourse>
        <PlusCourseCard
          course={course}
          key={course.uid}
          toggleEnroll={toggleEnroll(course)}
          disabledTopicGroup={isPlatinumEducator}
          hideLessonCount={isPlatinumEducator}
          gridColumn="span 6"
          marginBottom={Spacings.SPACING_8B}
          label="Free Classes See all"
        />
      </PlusCourse>
    );
  };

  const selectFilter = (filter) => (option) => {
    const applyFilters = cloneDeep(filtersApplied);
    applyFilters[filter].id = option.id;
    let queryParams = '';
    if (applyFilters.goal.id) queryParams += `goal=${applyFilters.goal.id}`;
    if (applyFilters.filter_by.id)
      queryParams += `&type=${applyFilters.filter_by.id}`;
    if (applyFilters.show_only.id)
      queryParams += `&courses=${applyFilters.show_only.id}`;
    if (applyFilters.language.id)
      queryParams += `&language=${applyFilters.language.id}`;
    if (applyFilters.topic.id) queryParams += `&topic=${applyFilters.topic.id}`;
    router.push(
      `/[authorID]/courses/?${queryParams}`,
      `/${authorID}/courses/?${queryParams}`,
      { shallow: true }
    );
  };

  const isFilterSelected = (filter) => {
    if (filter.key === 'goal' && query.goal) return true;
    if (filter.key === 'filter_by' && query.type) return true;
    if (filter.key === 'show_only' && query.courses) return true;
    if (query[filter.key]) return true;
    return false;
  };

  const loadMoreCourses = () => {
    const hasNextPage = !isReachingEnd(filteredCoursesResponse);
    if (hasNextPage) {
      setNumberOfPages((previousSize) => previousSize + 1);
    }
  };

  const { goal, type, courses, language, topic } = query;

  const [sort, setSort] = useState(null);

  useEffect(() => {
    if (
      !isFetchingFilteredCourses &&
      Array.isArray(filteredCoursesResponse?.sorts) &&
      !filteredCoursesResponse.sorts.length
    ) {
      setSort(null);
      setSortsApplied(null);
    }
  }, [
    filteredCoursesResponse?.sorts,
    isFetchingFilteredCourses,
    setSortsApplied,
    updateCoursesListData
  ]);

  useLayoutEffect(() => {
    function stickyFilter() {
      if (hideFilters) return null;
      if (window.pageYOffset > 106) {
        filterWrapperRef.current.classList.add('sticky');
      } else {
        filterWrapperRef.current.classList.remove('sticky');
      }
    }
    window.addEventListener('scroll', stickyFilter);
    return () => {
      window.removeEventListener('scroll', stickyFilter);
    };
  }, [hideFilters]);

  const selectSort = (sortBy) => (option) => {
    setNumberOfPages(1);
    setSort(option);
    setSortsApplied({ [sortBy]: option });
    refetchingIntervalRef.current = setTimeout(
      () => updateCoursesListData(),
      300
    );
  };
  const dataKey = `${userName}_${goal}_${type}_${courses}_${language}_${topic}_${
    sort?.id || 'relevance'
  }`;

  useEffect(() => {
    return () => {
      // Clear timeout to avoid memory leaks.
      if (refetchingIntervalRef.current)
        refetchingIntervalRef.current = clearInterval(
          refetchingIntervalRef.current
        );
    };
  }, []);

  const Loader = () => <CourseCarouselSkeleton number={4} />;

  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={message}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Dialog
        onClose={onCloseBatchUnenrollModal}
        dialogState={batchUnEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={batchUnEnrollMessage}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClickBatch}
        rejectOnClick={onCloseBatchUnenrollModal}
      />
      <TopWrapper $isFilters={hideFilters}>
        <Header>
          <BreadCrumbs tags={[]} onBackClick={goBack} />
          <Educator>{`${profileInfo?.firstName || ''} ${
            profileInfo?.lastName || ''
          }'s ${titleType}`}</Educator>
        </Header>
        {!hideFilters && (
          <FilterWrapper ref={filterWrapperRef}>
            {filteredCoursesResponse?.filters?.map((filter) => {
              const relevantOptions = filtersApplied[filter.key];
              const selectedOption = filter.values.find(
                (value) => relevantOptions.id === value.id
              );
              return (
                <Dropdown
                  options={filter.values}
                  key={filter.key}
                  selectedOption={selectedOption || filter.values[0]}
                  onOptionClick={selectFilter(filter.key)}
                  stripLength={getStripLength[filter.key]}
                  showSelectedBorder={isFilterSelected(filter)}
                  menuWidth="176px"
                />
              );
            })}
            {filteredCoursesResponse?.sorts?.map?.((filter, index) => (
              <Dropdown
                options={filter.values}
                key={`${filter.key}-${dataKey}`}
                selectedOption={
                  sort || filteredCoursesResponse?.sorts?.[index]?.values?.[0]
                }
                gridColumn="21/25"
                onOptionClick={selectSort(filter.key)}
                menuWidth="176px"
              />
            ))}
          </FilterWrapper>
        )}
      </TopWrapper>
      <InfiniteFeedRow
        data={filteredCoursesResponse?.results || []}
        isLoading={isFetchingFilteredCourses}
        onLoadMore={loadMoreCourses}
        renderItem={renderCourse}
        renderLoader={Loader}
      />
      {!isFetchingFilteredCourses &&
        filteredCoursesResponse?.results?.length === 0 &&
        renderEmptyState()}
    </>
  );
};
export default EducatorCourses;

EducatorCourses.propTypes = {
  authorID: PropTypes.string.isRequired,
  filtersApplied: PropTypes.objectOf(PropTypes.any).isRequired,
  profileInfo: PropTypes.objectOf(PropTypes.any),
  setSortsApplied: PropTypes.func.isRequired,
  filteredCoursesData: PropTypes.objectOf(PropTypes.any).isRequired,
  hideFilters: PropTypes.bool,
  titleType: PropTypes.string,
  isPlatinumEducator: PropTypes.bool
};

EducatorCourses.defaultProps = {
  profileInfo: {},
  hideFilters: false,
  titleType: 'courses',
  isPlatinumEducator: false
};
